import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { ChevronRight, Package2, Shield, Clock, MessageCircle, CheckCircle2, Rocket, BookOpen, Bot, Building2, Users, Globe2, Calculator } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import YuanRateCard from './YuanRateCard';
import RequestForm from './services/RequestForm';
import { useLanguage } from '../contexts/LanguageContext';

function HomePage() {
  const [expandedFaqs, setExpandedFaqs] = useState<Set<number>>(new Set([0]));
  const navigate = useNavigate();
  const [showRequestForm, setShowRequestForm] = useState(false);
  const { t, language } = useLanguage();

  // Add structured data for rich snippets
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "6069cargo",
    "url": "https://6069cargo.ru/",
    "logo": "https://i.ibb.co/9kCDBLTN/image.png",
    "description": "Организуем быструю доставку и надёжную логистику товаров из Китая для вашего бизнеса.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "ул. Примерная, д. 1",
      "addressLocality": "Москва",
      "postalCode": "123456",
      "addressCountry": "RU"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+7-999-123-4567",
      "contactType": "customer service",
      "availableLanguage": ["Russian", "English", "Chinese"]
    },
    "sameAs": [
      "https://facebook.com/kitlin",
      "https://twitter.com/kitlin",
      "https://instagram.com/kitlin"
    ]
  };

  // Add service structured data
  const serviceData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Доставка и экспедирование товаров из Китая",
    "provider": {
      "@type": "Organization",
      "name": "997cargo"
    },
    "serviceType": "Логистические услуги",
    "areaServed": {
      "@type": "Country",
      "name": "Россия"
    },
    "description": "Профессиональная доставка и экспедирование товаров из Китая. Комплексные логистические решения для бизнеса и частных лиц.",
    "offers": {
      "@type": "Offer",
      "price": "200",
      "priceCurrency": "CNY",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "200",
        "priceCurrency": "CNY",
        "unitCode": "KGM",
        "unitText": "кг"
      }
    }
  };

  // Preload hero section background image
  React.useEffect(() => {
    const img = new Image();
    img.src = 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>6069cargo - Организуем быструю доставку и надёжную логистику товаров из Китая для вашего бизнеса.</title>
        <meta name="description" content="Надёжная доставка и сопровождение грузов из Китая. Полный спектр логистических услуг для бизнеса. Более 10 лет на рынке и тысячи успешно выполненных заказов." />
        <meta name="keywords" content="доставка товаров из китая, экспедирование товаров из китая, логистика китай россия, карго из китая, поиск поставщиков в китае, таможенное оформление, грузоперевозки из китая" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        effect="fade"
        speed={1000}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        pagination={{
          clickable: true,
          renderBullet: function (index, className) {
            return `<span class="${className} custom-bullet"><span class="progress-bar"></span></span>`;
          },
        }}
        navigation
        loop
        className="h-screen"
        style={{
          '--swiper-navigation-color': '#fff',
          '--swiper-pagination-color': '#fff',
        }}
      >
        {/* First Slide */}
        <SwiperSlide>
          <section className="h-full pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0171E3]/30 via-[#0171E3]/20 to-transparent"></div>
          <div className="absolute inset-0 bg-[#000]/30"></div>
          
          {/* Animated shapes */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full border border-white/20 animate-spin-slow"></div>
            <div className="absolute bottom-1/3 right-1/4 w-24 h-24 border border-white/10 animate-float"></div>
            <div className="absolute top-2/3 left-1/3 w-0 h-0 border-l-[25px] border-l-transparent border-r-[25px] border-r-transparent border-b-[40px] border-b-white/5 animate-pulse-scale"></div>
          </div>
        </div>
        <div className="max-w-[980px] mx-auto relative text-center">
          <div className="max-w-[780px] mx-auto relative">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">{t('hero.title')}</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF] block">
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed mb-12">
              {t('hero.subtitle')}
            </p>
            <div className="hidden md:grid grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">10+</div>
                <div className="text-white/60">{t('hero.stats.experience')}</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">5000+</div>
                <div className="text-white/60">{t('hero.stats.clients')}</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">99.9%</div>
                <div className="text-white/60">{t('hero.stats.success')}</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setShowRequestForm(true)}
                className="inline-flex items-center bg-white text-[#1B3BA0] px-8 py-4 rounded-full hover:bg-[#99BBFF] transition-all font-medium text-lg group shadow-lg hover:shadow-xl"
              >
                <Package2 className="w-5 h-5 mr-2" />
                {t('hero.cta.order')}
                <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/prices')}
                className="inline-flex items-center bg-transparent text-white border-2 border-white/30 px-8 py-4 rounded-full hover:bg-white/10 transition-all font-medium text-lg group backdrop-blur-sm"
              >
                <Calculator className="w-5 h-5 mr-2" />
                {t('hero.cta.calculate')}
                <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
        </section>
        </SwiperSlide>

        {/* Logistics and Customs Slide */}
        <SwiperSlide>
          <section className="h-full pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0171E3]/30 via-[#0171E3]/20 to-transparent"></div>
              <div className="absolute inset-0 bg-[#000]/30"></div>
              
              {/* Animated elements */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-1/4 right-1/3 w-36 h-36 rounded-full border-2 border-white/10 animate-spin-slow"></div>
                <div className="absolute bottom-1/3 left-1/4 w-28 h-28 border border-white/20 rotate-45 animate-float"></div>
                <div className="absolute top-1/2 right-1/4 w-20 h-20 rounded-full border border-white/10 animate-pulse-scale"></div>
              </div>
            </div>
            <div className="max-w-[980px] mx-auto relative text-center">
              <div className="max-w-[780px] mx-auto relative">
                <h2 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
                  <span className="text-white block mb-2">{t('logistics.title')}</span>
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#4CAF50] to-[#8BC34A] animate-gradient-x block">
                    {t('logistics.subtitle')}
                  </span>
                </h2>
                <p className="text-xl md:text-2xl text-white/80 leading-relaxed mb-12">
                  {t('logistics.description')}
                </p>
                <div className="hidden md:grid grid-cols-3 gap-8 mb-12">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="text-2xl font-bold text-white mb-2">10-25</div>
                    <div className="text-white/60">{t('logistics.stats.delivery')}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="text-2xl font-bold text-white mb-2">24/7</div>
                    <div className="text-white/60">{t('logistics.stats.tracking')}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="text-2xl font-bold text-white mb-2">100%</div>
                    <div className="text-white/60">{t('logistics.stats.insurance')}</div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <a 
                    href="/services/business/logistics"
                    className="inline-flex items-center bg-white text-[#1B3BA0] px-8 py-4 rounded-full hover:bg-[#99BBFF] transition-all font-medium text-lg group shadow-lg hover:shadow-xl"
                  >
                    {t('logistics.cta')}
                    <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </section>
        </SwiperSlide>

        {/* Inspection Control Slide */}
        <SwiperSlide>
          <section className="h-full pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0171E3]/30 via-[#0171E3]/20 to-transparent"></div>
              <div className="absolute inset-0 bg-[#000]/30"></div>
              
              {/* Animated elements */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-1/3 left-1/4 w-32 h-32 border-2 border-white/10 rotate-45 animate-float"></div>
                <div className="absolute bottom-1/4 right-1/3 w-24 h-24 rounded-full border border-white/20 animate-spin-slow"></div>
                <div className="absolute top-1/2 left-1/3 w-16 h-16 border border-white/10 animate-pulse-scale"></div>
              </div>
            </div>
            <div className="max-w-[980px] mx-auto relative text-center">
              <div className="max-w-[780px] mx-auto relative">
                <h2 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
                  <span className="text-white block mb-2">{t('inspection.title')}</span>
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FF9800] to-[#FF5722] animate-gradient-x block">
                    {t('inspection.subtitle')}
                  </span>
                </h2>
                <p className="text-xl md:text-2xl text-white/80 leading-relaxed mb-12">
                  {t('inspection.description')}
                </p>
                <div className="hidden md:grid grid-cols-3 gap-8 mb-12">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="text-2xl font-bold text-white mb-2">1-3</div>
                    <div className="text-white/60">{t('inspection.stats.check')}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="text-2xl font-bold text-white mb-2">100+</div>
                    <div className="text-white/60">{t('inspection.stats.parameters')}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="text-2xl font-bold text-white mb-2">0%</div>
                    <div className="text-white/60">{t('inspection.stats.defects')}</div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <a 
                    href="/services/business/forwarding"
                    className="inline-flex items-center bg-white text-[#1B3BA0] px-8 py-4 rounded-full hover:bg-[#99BBFF] transition-all font-medium text-lg group shadow-lg hover:shadow-xl"
                  >
                    {t('inspection.cta')}
                    <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </section>
        </SwiperSlide>

        {/* Payment to Suppliers Slide */}
        <SwiperSlide>
          <section className="h-full pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1580519542036-c47de6196ba5?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0171E3]/30 via-[#0171E3]/20 to-transparent"></div>
              <div className="absolute inset-0 bg-[#000]/30"></div>
              
              {/* Animated elements */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-1/4 left-1/3 w-40 h-40 rounded-full border border-white/10 animate-spin-slow"></div>
                <div className="absolute bottom-1/3 right-1/4 w-28 h-28 border-2 border-white/20 rotate-45 animate-float"></div>
                <div className="absolute top-1/2 left-1/4 w-20 h-20 border border-white/10 animate-pulse-scale"></div>
              </div>
            </div>
            <div className="max-w-[980px] mx-auto relative text-center">
              <div className="max-w-[780px] mx-auto relative">
                <h2 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
                  <span className="text-white block mb-2">{t('payment.title')}</span>
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#9C27B0] to-[#E91E63] animate-gradient-x block">
                    {t('payment.subtitle')}
                  </span>
                </h2>
                <p className="text-xl md:text-2xl text-white/80 leading-relaxed mb-12">
                  {t('payment.description')}
                </p>
                <div className="hidden md:grid grid-cols-3 gap-8 mb-12">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="text-2xl font-bold text-white mb-2">1-2</div>
                    <div className="text-white/60">{t('payment.stats.transfer')}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="text-2xl font-bold text-white mb-2">0%</div>
                    <div className="text-white/60">{t('payment.stats.commission')}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="text-2xl font-bold text-white mb-2">100%</div>
                    <div className="text-white/60">{t('payment.stats.guarantee')}</div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <a 
                    href="/services/business/payment"
                    className="inline-flex items-center bg-white text-[#1B3BA0] px-8 py-4 rounded-full hover:bg-[#99BBFF] transition-all font-medium text-lg group shadow-lg hover:shadow-xl"
                  >
                    {t('payment.cta')}
                    <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </section>
        </SwiperSlide>
        {/* Second Slide */}
        <SwiperSlide>
          <section className="h-full pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0171E3]/30 via-[#0171E3]/20 to-transparent"></div>
          <div className="absolute inset-0 bg-[#000]/30"></div>
          
          {/* Animated elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full border-2 border-white/10 animate-pulse-scale"></div>
            <div className="absolute bottom-1/4 left-1/3 w-32 h-32 border border-white/20 rotate-45 animate-float"></div>
            <div className="absolute top-1/2 right-1/3 w-24 h-24 rounded-full border border-white/10 animate-spin-slow"></div>
          </div>
        </div>
        <div className="max-w-[980px] mx-auto relative text-center">
          <div className="max-w-[780px] mx-auto relative">
            <h2 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">{t('search.title')}</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FFD700] to-[#FF4500] animate-gradient-x block">
                {t('search.subtitle')}
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed mb-12">
              {t('search.description')}
            </p>
            <div className="hidden md:grid grid-cols-3 gap-8 mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="text-2xl font-bold text-white mb-2">{t('search.stats.search')}</div>
                <div className="text-white/60">{t('search.stats.search.desc')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="text-2xl font-bold text-white mb-2">{t('search.stats.check')}</div>
                <div className="text-white/60">{t('search.stats.check.desc')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="text-2xl font-bold text-white mb-2">{t('search.stats.delivery')}</div>
                <div className="text-white/60">{t('search.stats.delivery.desc')}</div>
              </div>
            </div>
            <div className="flex justify-center">
              <a 
                href="/services/business/search"
                className="inline-flex items-center bg-white text-[#1B3BA0] px-8 py-4 rounded-full hover:bg-[#99BBFF] transition-all font-medium text-lg group shadow-lg hover:shadow-xl"
              >
                {t('search.cta')}
                <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
        </section>
        </SwiperSlide>
      </Swiper>

      <section className="py-24 px-6" style={{ contentVisibility: 'auto', containIntrinsicSize: '600px' }}>
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">{t('advantages.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Package2 className="w-16 h-16 text-[#1B3BA0]" />,
                title: t('advantages.logistics.title'),
                description: t('advantages.logistics.desc')
              },
              {
                icon: <Shield className="w-16 h-16 text-[#1B3BA0]" />,
                title: t('advantages.security.title'),
                description: t('advantages.security.desc')
              },
              {
                icon: <Clock className="w-16 h-16 text-[#1B3BA0]" />,
                title: t('advantages.speed.title'),
                description: t('advantages.speed.desc')
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex flex-col items-center">
                  <div className="mb-6 bg-[#1B3BA0]/10 p-4 rounded-2xl">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{feature.title}</h3>
                  <p className="text-[#666] text-center">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Solutions */}
      <section className="py-24 px-6 bg-[#1B3BA0]/5" style={{ contentVisibility: 'auto', containIntrinsicSize: '600px' }}>
        <div className="max-w-[980px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1B3BA0] mb-6">{t('business.title')}</h2>
            <p className="text-xl text-[#666] max-w-2xl mx-auto">
              {t('business.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: <Building2 className="w-16 h-16 text-[#1B3BA0]" />,
                title: t('business.large.title'),
                features: language === 'ru' ? [
                  'Персональный менеджер 24/7',
                  'Прямые контракты с фабриками',
                  'Особые условия доставки',
                  'Таможенное оформление'
                ] : t('business.large.features')
              },
              {
                icon: <Users className="w-16 h-16 text-[#1B3BA0]" />,
                title: t('business.small.title'),
                features: language === 'ru' ? [
                  'Поиск надежных поставщиков',
                  'Выкуп товаров',
                  'Консолидация грузов',
                  'Оптимальные тарифы'
                ] : t('business.small.features')
              }
            ].map((solution, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex flex-col items-center mb-8">
                  <div className="mb-6 bg-[#1B3BA0]/10 p-4 rounded-2xl">
                    {solution.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-[#1B3BA0]">{solution.title}</h3>
                </div>
                <ul className="space-y-4 bg-[#1B3BA0]/5 p-6 rounded-xl">
                  {solution.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-[#666] font-medium">
                      <CheckCircle2 className="w-5 h-5 text-[#1B3BA0] mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            {t('services.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: t('services.cargo.title'),
                description: t('services.cargo.desc'),
                timing: t('services.cargo.timing'),
                image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&q=80'
              },
              {
                title: t('services.currency.title'),
                description: t('services.currency.desc'),
                timing: t('services.currency.timing'),
                image: 'https://images.unsplash.com/photo-1580519542036-c47de6196ba5?auto=format&fit=crop&q=80'
              },
              {
                title: t('services.translator.title'),
                description: t('services.translator.desc'),
                timing: t('services.translator.timing'),
                image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80'
              },
              {
                title: t('services.customs.title'),
                description: t('services.customs.desc'),
                timing: t('services.customs.timing'),
                image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80'
              },
              {
                title: t('services.suppliers.title'),
                description: t('services.suppliers.desc'),
                timing: t('services.suppliers.timing'),
                image: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&q=80'
              },
              {
                title: t('services.quality.title'),
                description: t('services.quality.desc'),
                timing: t('services.quality.timing'),
                image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&q=80'
              },
              {
                title: t('services.samples.title'),
                description: t('services.samples.desc'),
                timing: t('services.samples.timing'),
                image: 'https://images.unsplash.com/photo-1580674285054-bed31e145f59?auto=format&fit=crop&q=80'
              },
              {
                title: t('services.containers.title'),
                description: t('services.containers.desc'),
                timing: t('services.containers.timing'),
                image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&q=80'
              },
              {
                title: t('services.manufacturing.title'),
                description: t('services.manufacturing.desc'),
                timing: t('services.manufacturing.timing'),
                image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&q=80'
              },
              {
                title: t('services.marketplaces.title'),
                description: t('services.marketplaces.desc'),
                timing: t('services.marketplaces.timing'),
                image: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&q=80'
              },
              {
                title: t('services.storage.title'),
                description: t('services.storage.desc'),
                timing: t('services.storage.timing'),
                image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&q=80'
              },
              {
                title: t('services.competitors.title'),
                description: t('services.competitors.desc'),
                timing: t('services.competitors.timing'),
                image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80'
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                <div className="w-full h-48 rounded-xl overflow-hidden mb-6">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-xl font-bold text-[#1B3BA0] mb-4 h-[56px] flex items-center">{service.title}</h3>
                <div className="flex flex-col h-[200px]">
                  <p className="text-gray-600 mb-auto line-clamp-4">{service.description}</p>
                  <div className="mt-4">
                    <span className="text-sm text-gray-500">
                      <Clock className="w-4 h-4 inline-block mr-2" />
                      {service.timing}
                    </span>
                    <div className="mt-3">
                      <button
                        onClick={() => setShowRequestForm(true)}
                        className="text-[#1B3BA0] hover:text-[#162D7A] font-medium text-sm flex items-center"
                      >
                        {t('services.cta')}
                        <ChevronRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section for SEO */}
      <section className="py-24 px-6 bg-white" style={{ contentVisibility: 'auto', containIntrinsicSize: '600px' }}>
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-3xl font-semibold text-center text-[#1d1d1f] mb-12">
            {t('faq.title')}
          </h2>
          
          {/* FAQ Accordion */}
          <div className="space-y-6">
            {[
              {
                question: t('faq.delivery.question'),
                answer: t('faq.delivery.answer')
              },
              {
                question: t('faq.forwarding.question'),
                answer: t('faq.forwarding.answer')
              },
              {
                question: t('faq.destination.question'),
                answer: t('faq.destination.answer')
              },
              {
                question: t('faq.regions.question'),
                answer: t('faq.regions.answer')
              },
              {
                question: t('faq.commission.question'),
                answer: t('faq.commission.answer')
              },
              {
                question: t('faq.packaging.question'),
                answer: t('faq.packaging.answer')
              },
              {
                question: t('faq.cost.question'),
                answer: t('faq.cost.answer')
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
                onClick={() => {
                  const newExpandedFaqs = new Set(expandedFaqs);
                  if (newExpandedFaqs.has(index)) {
                    newExpandedFaqs.delete(index);
                  } else {
                    newExpandedFaqs.clear(); // Close other FAQs
                    newExpandedFaqs.add(index);
                  }
                  setExpandedFaqs(newExpandedFaqs);
                }}
              >
                <div className="p-6 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-[#1d1d1f] pr-8">
                      {faq.question}
                    </h3>
                    <ChevronRight 
                      className={`w-5 h-5 text-[#86868b] transition-transform duration-300 flex-shrink-0 ${
                        expandedFaqs.has(index) ? 'rotate-90' : ''
                      }`}
                    />
                  </div>
                  <div 
                    className={`transition-all duration-300 ${
                      expandedFaqs.has(index) ? 'mt-4 max-h-[1000px]' : 'max-h-0 overflow-hidden'
                    }`}
                  >
                    <p className="text-[#86868b] leading-relaxed whitespace-pre-line">
                      {faq.question === t('faq.cost.question') ? (
                        <>
                          {language === 'ru' ? 'Стоимость доставки зависит от категории товара, его плотности и выбранного типа доставки, поэтому назвать точную цену без уточнений невозможно.' : 'The cost of delivery depends on the category of goods, their density and the selected type of delivery, so it is impossible to name an exact price without clarification.'}
                          {'\n\n'}
                          {language === 'ru' ? 'На странице' : 'On the'}{' '}
                          <button 
                            onClick={() => navigate('/prices')}
                            className="text-[#1B3BA0] hover:text-[#0F2875] transition-colors inline-flex items-center"
                          >
                            👉 {t('faq.prices')}
                          </button>
                          {' '}{language === 'ru' ? 'мы показали примерную стоимость доставки по одной из популярных категорий (одежда) за 2024 год. Это поможет вам ориентироваться в диапазоне цен.' : 'we showed the approximate cost of delivery for one of the popular categories (clothing) for 2024. This will help you navigate the price range.'}
                          {'\n\n'}
                          {language === 'ru' ? 'Чтобы узнать актуальную цену именно для вашего груза —' : 'To find out the current price for your cargo —'}{' '}
                          <button 
                            onClick={() => setShowRequestForm(true)}
                            className="text-[#1B3BA0] hover:text-[#0F2875] transition-colors inline-flex items-center"
                          >
                            {t('faq.request')}
                          </button>
                          {language === 'ru' ? ', и наш менеджер свяжется с вами, уточнит параметры заказа и предоставит персональное коммерческое предложение в течение 5 минут.' : ', and our manager will contact you, clarify the order parameters and provide a personal commercial offer within 5 minutes.'}
                        </>
                      ) : faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {t('contact.title')}
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
          <RequestForm source={language === 'ru' ? "Главная страница" : "Home Page"} />
        </div>
      </section>
      
      {/* Request Form Popup */}
      {showRequestForm && (
        <RequestForm 
          isPopup 
          source={language === 'ru' ? "Главная страница" : "Home Page"}
          onClose={() => setShowRequestForm(false)} 
        />
      )}
      <style>{`
        .swiper {
          width: 100%;
          height: 100vh; 
        }
        
        .swiper-slide {
          height: 100vh;
        }
        
        @media (max-width: 768px) {
          .swiper, .swiper-slide {
            height: 90vh;
          }
        }
        
        .swiper-button-next,
        .swiper-button-prev {
          color: white !important;
          display: none;
          transition: opacity 0.3s ease;
          width: 50px !important;
          height: 50px !important;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 50%;
        }
        
        @media (min-width: 768px) {
          .swiper-button-next,
          .swiper-button-prev {
            display: block;
            opacity: 0;
          }
        }
        
        .swiper-button-next:hover,
        .swiper-button-prev:hover {
          opacity: 1;
        }
        
        .swiper-button-next::after,
        .swiper-button-prev::after {
          font-size: 20px !important;
        }
        
        .swiper-pagination {
          bottom: 40px !important;
        }
        
        @media (max-width: 768px) {
          .swiper-pagination {
            bottom: 20px !important;
          }
        }
        
        .custom-bullet {
          width: 40px !important;
          height: 4px !important;
          border-radius: 2px !important;
          background: rgba(255, 255, 255, 0.3) !important;
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }
        
        @media (max-width: 768px) {
          .custom-bullet {
            width: 30px !important;
            height: 3px !important;
          }
          
          .custom-bullet.swiper-pagination-bullet-active {
            width: 45px !important;
          }
        }
        
        .custom-bullet.swiper-pagination-bullet-active {
          background: white !important;
          width: 60px !important;
        }

        .custom-bullet .progress-bar {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          width: 100%;
          background: #1B3BA0;
          transform: translateX(-100%);
        }

        .custom-bullet.swiper-pagination-bullet-active .progress-bar {
          animation: progress 5s linear forwards;
        }

        @keyframes progress {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }
        
        @keyframes gradient-x {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .animate-gradient-x {
          background-size: 200% auto;
          animation: gradient-x 4s ease infinite;
        }
      `}</style>
    </div>
  );
}

export default HomePage;